/**
 * tasks.service.ts
 *
 * Typed API call wrappers for the Tasks domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 * Returns unwrapped data — callers never see raw Axios response wrappers.
 */
import { addWeeks, addMonths, format } from 'date-fns';
import { apiClient } from './apiClient';
import { parseDateValue } from '@/lib/utils';
import { todayInTz } from '@/lib/facilityTimezone';
import type { PaginatedResponse, ApiResponse } from '@/types/api.types';

// ─── Domain types (mirrors server tasks.types.ts) ─────────────────────────────

export type TaskStatus = 'pending' | 'in_progress' | 'complete' | 'cancelled' | 'overdue';
export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent';
export type TaskType = 'manual' | 'email' | 'sms' | 'document' | 'call' | 'other';

/** Mirrors plan_tasks row from the server */
export interface Task {
  planTaskID: number;
  planID: number | null;
  taskItemID: number | null;
  taskID: number | null;
  name: string;
  timeType: number | null;
  timeAmount: number | null;
  assignedToID: string | null;
  priority: number;            // 1=Low, 2=Medium, 3=High
  recurring: number;           // 0=Once, 1=Recurring
  active: number;
  dateStarted: string | null;
  dateCompleted: string | null;
  lastDue: string | null;
  pending: number;
  required: number;
  notes: string | null;
  accountID: number;
  customDate: string | null;
  planDisplayID: string | null;
  taskPatientID: number | null;
  userID: number | null;
  sendToID: number | null;
  recurringType: number | null;
  recurringAmount: number | null;
  // Legacy plan_tasks columns required for full modal parity
  autoTaskID: number | null;       // 0=Human, 1=Email, 2=SMS, 3-5=Opt-out, 6=Tag, 7=Email Survey, 8=Stage, 9=SMS Survey
  headerFooterID: number | null;   // FK email_header_footer.emailHeaderFooterID OR (when autoTaskID=7|9) surveys.surveysID
  contentID: number | null;        // FK email_content.emailContentID / sms.smsID OR (when autoTaskID=6) tags_custom.tagsCustomID
  attachments: string | null;      // CSV: 'P_<packetID>' for packets, '<documentID>' for files
  noteID: number | null;
  dateType: number | null;         // 1-9 (see legacy lines 813-839)
  recurringNumber: number | null;  // 2-100
  cycleCompleted: number | null;
  // Joined fields
  patientName?: string | null;
  patientFirstName?: string | null;
  patientLastName?: string | null;
  /**
   * Patient the task belongs to, resolved server-side as taskPatientID with a
   * fallback to the linked plan's patient. Display/link only — save paths still
   * write `taskPatientID`. Only returned by GET /plan-tasks/{id}.
   */
  patientID?: number | null;
  assignedToName?: string | null;
  assignedToImage?: string | null;
  facilityName?: string | null;
  /** 1 when the patient has an uncancelled membership record, else 0. */
  memberStatus?: number;
  /** plan_types.name for the linked plan. Only returned by the task list endpoints. */
  planTypeName?: string | null;
  derivedStatus?: string;      // 'Past Due' | 'Due' | 'Upcoming' | 'Completed' | 'Inactive'
  // Compat aliases (used by dashboard)
  taskName?: string | null;
  title?: string;
  status?: string;
  dueDate?: string | null;
  groupName?: string | null;
}

export interface TaskTemplate {
  taskID: number;
  accountID: number;
  name: string;
  type: TaskType;
  description: string | null;
  notes: string | null;
  active: number;
  defaultPriority: TaskPriority;
  defaultDueDays: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface TaskGroup {
  taskGroupID: number;
  accountID: number;
  name: string;
  description: string | null;
  active: number;
  required: number;
  planTypeID: number | null;
  doctorID: number | null;
  coordID: number | null;
  nurseID: number | null;
  facilityID: number | null;
  procedureID: number | null;
  leadSourceID: number | null;
  tagID: number | null;
  appointmentTypeID: number | null;
  addAtStage: number | null;
  removeAtStage: number | null;
  createdAt: string;
  updatedAt: string;
  itemCount?: number;
}

export interface TaskGroupItem {
  taskGroupItemID: number;
  accountID: number;
  taskGroupID: number;
  taskID: number;
  sortOrder: number;
  dueDays: number | null;
  createdAt: string;
  taskName?: string;
  taskType?: TaskType;
}

export interface TaskRuleTrigger {
  id: number;
  label: string;
}

export interface TaskRuleFilter {
  key: string;
  label: string;
  values: string[];
  rawValues: string[];
}

export interface TaskRuleStoredFilter {
  key: string;
  label: string;
  rawCsv: string;
  values: string[];
  labels: string[];
  isWildcard: boolean;
}

export interface TaskRuleTemplateRefs {
  contentID: number;
  headerFooterID: number;
  sendToID: number;
  attachments: string | null;
}

export interface TaskRuleTask {
  taskItemID: number;
  taskID: number;
  name: string;
  active: boolean;
  when: string;
  recurrence: string;
  assignedTo: string;
  priority: string;
  actionType: number;
  actionTypeLabel: string;
  actionSummary: string;
  notes: string | null;
  templateRefs: TaskRuleTemplateRefs;
}

export interface TaskRuleGroup {
  taskGroupID: number;
  name: string;
  active: boolean;
  required: boolean;
  applyToPlan: boolean;
  /** Appointment Stage only: tasks are added only when the appointment is marked First Time. */
  firstAppointmentOnly?: boolean;
  triggerType: number;
  triggerTypeLabel: string;
  triggerDetails: string;
  assignTriggers: TaskRuleTrigger[];
  removeTriggers: TaskRuleTrigger[];
  filters: TaskRuleFilter[];
  storedFilters: Record<string, TaskRuleStoredFilter>;
  tasks: TaskRuleTask[];
}

/** Row from GET /task-group-filters (id + display name). */
export interface TaskGroupFilterRow {
  id: string | number;
  name: string;
}

/**
 * Payload from GET /api/v5/task-group-filters?type=all
 * (keys mirror TaskGroupsController::filters).
 */
export interface TaskGroupFiltersPayload {
  stages?: TaskGroupFilterRow[];
  planTypes?: TaskGroupFilterRow[];
  doctors?: TaskGroupFilterRow[];
  coordinators?: TaskGroupFilterRow[];
  nurses?: TaskGroupFilterRow[];
  facilities?: TaskGroupFilterRow[];
  procedures?: TaskGroupFilterRow[];
  leadSources?: TaskGroupFilterRow[];
  languages?: TaskGroupFilterRow[];
  appointmentTypes?: TaskGroupFilterRow[];
  appointmentTypeGroups?: TaskGroupFilterRow[];
  patientTags?: TaskGroupFilterRow[];
  products?: TaskGroupFilterRow[];
  memberships?: TaskGroupFilterRow[];
  packages?: TaskGroupFilterRow[];
  stageSubs?: TaskGroupFilterRow[];
  appointmentStatuses?: TaskGroupFilterRow[];
  roles?: TaskGroupFilterRow[];
}

export interface TaskLog {
  taskLogID: number;
  accountID: number;
  taskItemID: number | null;
  taskID: number | null;
  patientID: number | null;
  planID: number | null;
  action: string;
  performedByID: number | null;
  notes: string | null;
  createdAt: string;
  patientName?: string | null;
  performedByName?: string | null;
  taskName?: string | null;
}

export interface TaskFilterPreset {
  filterPresetID: number;
  accountID: number;
  userID: number;
  name: string;
  filters: string;
  createdAt: string;
}

export interface TaskTypeOption {
  value: TaskType;
  label: string;
}

// ─── Request payload types ────────────────────────────────────────────────────

export interface TaskCounts {
  all: number;
  upcoming: number;
  /** Legacy alias of `upcoming` — future-dated pending tasks only. */
  pending: number;
  /** Past Due + Due Today + Upcoming, i.e. everything still awaiting action. */
  actionable: number;
  dueToday: number;
  pastDue: number;
  completed: number;
  inactive: number;
}

const EMPTY_TASK_COUNTS: TaskCounts = {
  all: 0,
  upcoming: 0,
  pending: 0,
  actionable: 0,
  dueToday: 0,
  pastDue: 0,
  completed: 0,
  inactive: 0,
};

function normalizeTaskCounts(value: unknown): TaskCounts {
  const counts = (value && typeof value === 'object' ? value : {}) as Partial<TaskCounts>;
  const upcoming = typeof counts.upcoming === 'number'
    ? counts.upcoming
    : (typeof counts.pending === 'number' ? counts.pending : 0);
  const dueToday = typeof counts.dueToday === 'number' ? counts.dueToday : 0;
  const pastDue = typeof counts.pastDue === 'number' ? counts.pastDue : 0;

  return {
    all: typeof counts.all === 'number' ? counts.all : 0,
    upcoming,
    pending: upcoming,
    // Sum the three buckets when the server didn't send `actionable`; the
    // buckets are mutually exclusive, so the total is identical.
    actionable: typeof counts.actionable === 'number'
      ? counts.actionable
      : pastDue + dueToday + upcoming,
    dueToday,
    pastDue,
    completed: typeof counts.completed === 'number' ? counts.completed : 0,
    inactive: typeof counts.inactive === 'number' ? counts.inactive : 0,
  };
}

/**
 * Anchor to Pacific (America/Los_Angeles) so manual task-create defaults match
 * the backend, which computes lastDue/dateStarted in Pacific. Using UTC via
 * toISOString() rolled to "tomorrow" after 5pm PST.
 */
function todayIsoDate(): string {
  return todayInTz('America/Los_Angeles');
}

function priorityToNumber(priority?: TaskPriority | number | null): number | undefined {
  if (typeof priority === 'number') {
    return priority;
  }

  switch (priority) {
    case 'high':
    case 'urgent':
      return 3;
    case 'normal':
      return 2;
    case 'low':
      return 1;
    default:
      return undefined;
  }
}

function buildPlanTaskPayload(data: CreateTaskPayload | UpdateTaskPayload): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  if ('title' in data && data.title !== undefined) {
    payload.name = data.title;
  }

  if (data.notes !== undefined) {
    payload.notes = data.notes;
  }

  const priority = priorityToNumber(data.priority);
  if (priority !== undefined) {
    payload.priority = priority;
  }

  if (data.dueDate !== undefined) {
    payload.lastDue = data.dueDate || null;
  }

  if (data.assignedToID !== undefined) {
    if (data.assignedToID == null) {
      payload.assignedToID = null;
      payload.sendToID = null;
      payload.userID = null;
    } else {
      payload.assignedToID = 'user';
      payload.sendToID = data.assignedToID;
      payload.userID = data.assignedToID;
    }
  }

  if ('facilityID' in data && data.facilityID !== undefined) {
    payload.facilityID = data.facilityID;
  }

  return payload;
}

/**
 * Who a task is for: `automatic` is a task assigned to Prospera
 * (assignedToID 'ftp'), `human` is every other task, `all` filters nothing.
 */
export type TaskSource = 'human' | 'automatic' | 'all';

export interface TaskFiltersParams {
  // 1=All, 2=Upcoming, 3=Due Today, 4=Past Due, 5=Completed, 6=Inactive,
  // 11=Pending/actionable (Past Due + Due Today + Upcoming — TASK_BUCKET_PENDING)
  flTasks?: number;
  assigneeId?: number;
  patientId?: number;
  planId?: number;
  /** Omitted filters nothing, the same as `all`. */
  taskSource?: TaskSource;
  typeId?: TaskType;
  status?: TaskStatus;
  priority?: TaskPriority;
  priorities?: TaskPriority[];
  prioritiesNegate?: boolean;
  groupId?: number;
  groupIds?: number[];
  groupIdsNegate?: boolean;
  facilityId?: number;
  facilityIds?: number[];
  facilityIdsNegate?: boolean;
  stageId?: number;
  stageIds?: number[];
  stageIdsNegate?: boolean;
  memberStatus?: string;
  memberStatuses?: string[];
  memberStatusesNegate?: boolean;
  userGroupId?: number;
  userGroupIds?: number[];
  userGroupIdsNegate?: boolean;
  /**
   * When true, the "Me" pseudo-option is selected in the Assigned To sidebar.
   * Backend treats this as: pt.userID = currentUser, OR'd with userGroupIds
   * if both are present. Mirrors legacy filter-assigned-to.php "me-{userID}".
   */
  userGroupIncludesMe?: boolean;
  /**
   * Typed Assigned-To tokens. Each value is one of:
   *   `me`                    -> pt.userID = currentUser
   *   `ftp`                   -> pt.assignedToID = 'ftp' (FollowThatPatient auto-tasks)
   *   `coord` | `nurse` | `doc` | `rec`
   *                           -> pt.assignedToID = '<token>' (plan staff slots)
   *   `user-<userId>`         -> pt.assignedToID = 'user' AND pt.sendToID = userId
   *   `role-<roleId>`         -> pt.assignedToID IN ('group','group_<id>','role_<id>')
   *                              AND pt.sendToID = roleId
   * Supersedes userGroupId(s)/userGroupIncludesMe on the backend when supplied.
   */
  assignedToTokens?: string[];
  assignedToTokensNegate?: boolean;
  dueDateFrom?: string;
  dueDateTo?: string;
  dueDate?: string;
  search?: string;
  patientSearch?: string;
  membershipStatus?: string;
  membershipStatuses?: string[];
  membershipStatusesNegate?: boolean;
  referralSourceId?: number;
  referralSourceIds?: number[];
  referralSourceIdsNegate?: boolean;
  coordinatorId?: number;
  coordinatorIds?: number[];
  coordinatorIdsNegate?: boolean;
  tagId?: number;
  tagIds?: number[];
  tagIdsNegate?: boolean;
  createdFrom?: string;
  createdTo?: string;
  dobFrom?: string;
  dobTo?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CreateTaskPayload {
  taskID?: number;
  planID?: number;
  patientID?: number;
  taskGroupID?: number;
  title: string;
  description?: string;
  type: TaskType;
  priority?: TaskPriority;
  assignedToID?: number;
  dueDate?: string;
  notes?: string;
  /**
   * Optional facility scope for the new task. When omitted, the server falls
   * back to the linked plan/patient's facility (legacy behavior). Used by the
   * global-facility selector to silently scope newly created tasks to the
   * user's working facility.
   */
  facilityID?: number;
}

export interface UpdateTaskPayload {
  title?: string;
  description?: string;
  type?: TaskType;
  status?: TaskStatus;
  priority?: TaskPriority;
  assignedToID?: number | null;
  dueDate?: string | null;
  notes?: string;
}

/**
 * Wide payload mirroring backend UpdatePlanTaskRequest field-for-field.
 * Used by the full-parity Task Edit Modal (TasksPage.tsx) so every persisted
 * plan_tasks column can be sent. Values pass through untransformed; this is a
 * deliberate alternative to the narrow UpdateTaskPayload used elsewhere.
 */
export interface PlanTaskRawPayload {
  accountID?: number | null;
  planID?: number | null;
  taskItemID?: number | null;
  taskID?: number | null;
  name?: string | null;
  timeType?: number | null;
  timeAmount?: number | null;
  recurringType?: number | null;
  recurringAmount?: number | null;
  dateType?: number | null;
  assignedToID?: string | null;
  priority?: number | null;
  recurring?: number | null;
  active?: number | null;
  dateStarted?: string | null;
  dateCompleted?: string | null;
  lastDue?: string | null;
  pending?: number | null;
  required?: number | null;
  cycleCompleted?: number | null;
  notes?: string | null;
  autoTaskID?: number | null;
  headerFooterID?: number | null;
  contentID?: number | null;
  sendToID?: number | null;
  attachments?: string | null;
  recurringNumber?: number | null;
  customDate?: string | null;
  planDisplayID?: number | null;
  userID?: number | null;
  taskPatientID?: number | null;
  noteID?: number | null;
  /**
   * Create-only, never a plan_tasks column: staff picked by hand for this task.
   * The backend writes one row per entry and discards the list. Ignored unless
   * `assignedToID` is 'user'. See createPlanTaskRaw below.
   */
  assignedUserIDs?: number[];
}

export interface CompleteTaskPayload {
  /**
   * Free-form note persisted on the task and written to comm_log (when the
   * task is plan-linked). Both PATCH /tasks/{id}/complete and
   * PATCH /tasks/{id}/deactivate read this field as `notes` — see
   * TaskExtrasController (mirrors legacy task_set_complete.php:54).
   */
  notes?: string;
}

/**
 * Same body as a completion — De-Activate passes
 * `Manually de-activated by {firstName} {lastName}` as the note. Aliased
 * rather than merged so the two call sites read differently at a glance.
 */
export type DeactivateTaskPayload = CompleteTaskPayload;

export interface CreateTaskGroupPayload {
  name: string;
  description?: string;
  active?: number;
  required?: number;
  planTypeID?: number;
  doctorID?: number;
  coordID?: number;
  nurseID?: number;
  facilityID?: number;
  procedureID?: number;
  leadSourceID?: number;
  tagID?: number;
  appointmentTypeID?: number;
  addAtStage?: number;
  removeAtStage?: number;
}

export type UpdateTaskGroupPayload = Partial<CreateTaskGroupPayload>;

export interface TriggerGroupPayload {
  patientID: number;
  planID?: number;
}

export interface SaveFilterPayload {
  name: string;
  filters: Omit<TaskFiltersParams, 'page' | 'pageSize'>;
}

export interface TaskLogFiltersParams {
  userId?: number;
  patientId?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  perPage?: number;
}

// ─── Service methods ──────────────────────────────────────────────────────────

/**
 * Fetch a paginated list of tasks with optional filters.
 */
export function listTasks(
  params: TaskFiltersParams = {},
  options: { signal?: AbortSignal } = {},
): Promise<PaginatedResponse<Task>> {
  return apiClient
    .get<PaginatedResponse<Task>>('/plan-tasks', { params, signal: options.signal })
    .then((r) => r.data);
}

/**
 * Fetch a single task by ID.
 */
export function getTask(id: number): Promise<Task> {
  return apiClient
    .get<ApiResponse<Task>>(`/plan-tasks/${id}`)
    .then((r) => r.data.data);
}

// ─── Task change history ──────────────────────────────────────────────────────
// Shape intentionally mirrors PlanHistoryEntry (plans.service.ts) so the shared
// history-card rendering works unchanged.

export interface TaskHistoryChange {
  field: string;
  verb: string;
  newValue: string;
}

export interface TaskHistoryEntry {
  date: string;
  userId: number;
  userName: string;
  /** 'Created' for the first snapshot, 'Edited' for subsequent ones. */
  verb?: string;
  /** What caused the write: 'manual' | 'milestone' | 'appointment' | 'recurrence' | … */
  triggerType?: string;
  changes: TaskHistoryChange[];
}

/**
 * Fetch a task's field-change audit log (diffed plan_tasks_history snapshots).
 */
export function getTaskHistory(planTaskID: number): Promise<TaskHistoryEntry[]> {
  return apiClient
    .get<ApiResponse<TaskHistoryEntry[]>>(`/plan-tasks/${planTaskID}/history`)
    .then((r) => r.data.data);
}

/**
 * Create a new task.
 */
export function createTask(data: CreateTaskPayload): Promise<Task> {
  const payload = {
    ...buildPlanTaskPayload(data),
    planID: data.planID,
    taskPatientID: data.patientID,
    pending: 1,
    active: 1,
    recurring: 0,
    dateStarted: todayIsoDate(),
    lastDue: data.dueDate || todayIsoDate(),
  };

  return apiClient
    .post<ApiResponse<Task>>('/plan-tasks', payload)
    .then((r) => r.data.data);
}

/**
 * Update an existing task.
 */
export function updateTask(id: number, data: UpdateTaskPayload): Promise<Task> {
  if (data.status === 'complete') {
    return completeTask(id);
  }

  const payload = buildPlanTaskPayload(data);

  if (data.status === 'pending') {
    payload.pending = 1;
    payload.dateCompleted = null;
  }

  return apiClient
    .put<ApiResponse<Task>>(`/plan-tasks/${id}`, payload)
    .then((r) => r.data.data);
}

/**
 * Create a plan task with a wide payload (every plan_tasks column).
 * Used by the full-parity TaskModal. Skips the narrow buildPlanTaskPayload
 * conversion so caller controls every persisted field exactly.
 *
 * NOTE: the backend (PlanTasksController::createPlanTasksWithFanOut) fans this
 * out to N rows in two cases, both mirroring legacy plan-task-processor.php:98-119:
 *   - `payload.assignedToID` starts with "role_<X>" — one row per active member
 *     of role X.
 *   - `payload.assignedToID` is 'user' and `payload.assignedUserIDs` is a
 *     non-empty list — one row per listed user (AssignedUserListExpander).
 *     Declined for autoTaskID 1/2/7/9, where sendToID holds a send-target enum.
 * In both cases the response shape becomes `Task[]` with a meta.count instead
 * of a single Task. Callers like TaskModal that just close on success don't
 * need to inspect the shape; callers that do should handle both with
 * `Array.isArray(result)`.
 */
export function createPlanTaskRaw(payload: PlanTaskRawPayload): Promise<Task | Task[]> {
  return apiClient
    .post<ApiResponse<Task | Task[]>>('/plan-tasks', payload)
    .then((r) => r.data.data);
}

/**
 * Update a plan task with a wide payload (every plan_tasks column).
 * Used by the full-parity TaskModal. Skips the narrow buildPlanTaskPayload
 * conversion so caller controls every persisted field exactly.
 */
export function updatePlanTaskRaw(id: number, payload: PlanTaskRawPayload): Promise<Task> {
  return apiClient
    .put<ApiResponse<Task>>(`/plan-tasks/${id}`, payload)
    .then((r) => r.data.data);
}

/**
 * Push a task's due date to today + 1 week / 1 month (Pacific-anchored to match
 * the backend). Reuses PUT /plan-tasks/{id}, which writes an explicit lastDue
 * verbatim (no recompute) and snapshots plan_tasks_history as triggerType 'manual'.
 */
export function rescheduleTaskFromToday(id: number, unit: 'week' | 'month'): Promise<Task> {
  const base = parseDateValue(todayIsoDate()) ?? new Date(); // Pacific "today" at local midnight
  const next = unit === 'week' ? addWeeks(base, 1) : addMonths(base, 1);
  return updatePlanTaskRaw(id, { lastDue: format(next, 'yyyy-MM-dd') }); // local-safe, no toISOString drift
}

/**
 * Read-only triggers (stage + appt) for a single plan task.
 * Used by the TaskModal to display Assign/Remove at Stage and appt-status
 * triggers as disabled selects (legacy modal save path never persisted these,
 * so they are display-only here too \u2014 see plan-task-details2.php audit).
 */
export interface PlanTaskTriggers {
  addAtStage: number[];
  removeAtStage: number[];
  addAtApptStage: number[];
  removeAtApptStage: number[];
}

export function getPlanTaskTriggers(id: number): Promise<PlanTaskTriggers> {
  return apiClient
    .get<ApiResponse<PlanTaskTriggers>>(`/plan-tasks/${id}/triggers`)
    .then((r) => r.data.data);
}

/**
 * Mark a task as complete. Sets pending=0 + dateCompleted=today (status
 * 'Completed') and advances the next occurrence of a recurring task.
 */
export function completeTask(id: number, data: CompleteTaskPayload = {}): Promise<Task> {
  return apiClient
    .patch<ApiResponse<Task>>(`/tasks/${id}/complete`, data)
    .then((r) => r.data.data);
}

/**
 * De-activate a task: close it out WITHOUT recording a completion. Sets
 * pending=0 and leaves dateCompleted at the zero sentinel, so the task reads
 * as 'Inactive' rather than 'Completed', and terminates a recurring series
 * instead of advancing it.
 *
 * This used to POST to /complete with a canned note, which made every
 * de-activation count as a completed task — see
 * TaskExtrasController::deactivate for the full rationale.
 */
export function deactivateTask(id: number, data: DeactivateTaskPayload = {}): Promise<Task> {
  return apiClient
    .patch<ApiResponse<Task>>(`/tasks/${id}/deactivate`, data)
    .then((r) => r.data.data);
}

/**
 * Delete a task.
 */
export function deleteTask(id: number): Promise<void> {
  return apiClient.delete(`/plan-tasks/${id}`).then(() => undefined);
}

/**
 * Fetch tasks assigned to the current user.
 *
 * Pass `myOnly: true` to force the backend's legacy assignment scope
 * (pt.userID = me OR pt.assignedToID='group' AND pt.sendToID IN myRoles)
 * even when the caller is an admin (userLevel >= 3). Used by the
 * dashboard "Pending Tasks" card so admins see their own queue there.
 *
 * Pass `withTotal: false` when the caller shows no total or pager: the server
 * then skips its COUNT query (as slow as the list itself for a busy assignee)
 * and `meta.total` / `meta.totalPages` come back absent.
 */
export function getMyTasks(
  params: TaskFiltersParams & { perPage?: number; myOnly?: boolean; withTotal?: boolean } = {},
): Promise<PaginatedResponse<Task>> {
  return apiClient
    .get<PaginatedResponse<Task>>('/tasks/assigned', { params })
    .then((r) => r.data);
}

// ─── Grouped-by-patient types & fetcher ─────────────────────────────────────

/** A single task row inside a patient group (subset of Task fields). */
export interface PatientTaskGroupTask {
  planTaskID: number;
  planID: number | null;
  name: string;
  priority: number;
  recurring: number;
  active: number;
  dateStarted: string | null;
  dateCompleted: string | null;
  lastDue: string | null;
  pending: number;
  required: number;
  notes: string | null;
  facilityName: string | null;
  derivedStatus: string;
}

/** A patient-level summary row with embedded tasks for drill-down. */
export interface PatientTaskGroup {
  taskPatientID: number | null;
  patientName: string;
  memberStatus: number;
  facilityName: string;
  highestPriority: number;
  taskCount: number;
  earliestDue: string | null;
  latestDue: string | null;
  tasks: PatientTaskGroupTask[];
}

/**
 * Fetch tasks grouped by patient for the Past Due (flTasks=9) or
 * Upcoming (flTasks=10) tabs. Returns paginated patient groups.
 */
export function listTasksGroupedByPatient(
  params: TaskFiltersParams = {},
  options: { signal?: AbortSignal } = {},
): Promise<PaginatedResponse<PatientTaskGroup>> {
  return apiClient
    .get<PaginatedResponse<PatientTaskGroup>>('/tasks/grouped-by-patient', { params, signal: options.signal })
    .then((r) => r.data);
}

/**
 * Fetch task counts by filter category. Supports the same `myOnly`
 * opt-in as getMyTasks so the dashboard counts stay aligned with the
 * dashboard list.
 */
export function getTaskCounts(
  params: TaskFiltersParams & { myOnly?: boolean } = {},
  options: { signal?: AbortSignal } = {},
): Promise<TaskCounts> {
  return apiClient
    .get<ApiResponse<TaskCounts>>('/tasks/counts', { params, signal: options.signal })
    .then((r) => normalizeTaskCounts(r.data?.data))
    .catch(() => EMPTY_TASK_COUNTS);
}

/**
 * Fetch available task type enum values.
 */
export function getTaskTypes(): Promise<TaskTypeOption[]> {
  return apiClient
    .get<ApiResponse<TaskTypeOption[]>>('/tasks/types')
    .then((r) => r.data.data);
}

// ─── Task groups ──────────────────────────────────────────────────────────────

/**
 * List all task groups for the account.
 */
export function listGroups(
  params: { page?: number; perPage?: number } = {},
): Promise<PaginatedResponse<TaskGroup>> {
  return apiClient
    .get<PaginatedResponse<TaskGroup>>('/task-groups', { params })
    .then((r) => r.data);
}

/**
 * Task group filter dimension lists (plan types, doctors, etc.).
 * Same source as the task group editor.
 */
export function getTaskGroupFilters(
  type: string = 'all',
): Promise<TaskGroupFiltersPayload> {
  return apiClient
    .get<ApiResponse<TaskGroupFiltersPayload>>('/task-group-filters', {
      params: { type },
    })
    .then((r) => r.data.data);
}

/**
 * List read-only task rules for the account.
 */
export function listTaskRules(): Promise<TaskRuleGroup[]> {
  return apiClient
    .get<ApiResponse<TaskRuleGroup[]>>('/task-rules')
    .then((r) => r.data.data);
}

/**
 * Get a single task group with its items.
 */
export function getGroup(id: number): Promise<{ group: TaskGroup; items: TaskGroupItem[] }> {
  return apiClient
    .get<ApiResponse<{ group: TaskGroup; items: TaskGroupItem[] }>>(`/task-groups/${id}`)
    .then((r) => r.data.data);
}

/**
 * Create a new task group.
 */
export function createGroup(data: CreateTaskGroupPayload): Promise<TaskGroup> {
  return apiClient
    .post<ApiResponse<TaskGroup>>('/task-groups', data)
    .then((r) => r.data.data);
}

/**
 * Update an existing task group.
 */
export function updateGroup(id: number, data: UpdateTaskGroupPayload): Promise<TaskGroup> {
  return apiClient
    .put<ApiResponse<TaskGroup>>(`/task-groups/${id}`, data)
    .then((r) => r.data.data);
}

/**
 * Delete a task group and all its items.
 */
export function deleteGroup(id: number): Promise<void> {
  return apiClient.delete(`/task-groups/${id}`).then(() => undefined);
}

/**
 * Trigger a task group for a specific patient, creating all task items.
 * Returns the IDs of newly created task items.
 */
export function triggerGroupForPatient(
  groupId: number,
  data: TriggerGroupPayload,
): Promise<{ createdTaskItemIds: number[] }> {
  return apiClient
    .post<ApiResponse<{ createdTaskItemIds: number[] }>>(`/task-groups/${groupId}/trigger`, data)
    .then((r) => r.data.data);
}

// ─── Filter presets ───────────────────────────────────────────────────────────

/**
 * Get saved filter presets for the current user.
 */
export function getFilters(): Promise<TaskFilterPreset[]> {
  return apiClient
    .get<ApiResponse<TaskFilterPreset[]>>('/tasks/filters')
    .then((r) => r.data.data);
}

/**
 * Save a filter preset for the current user.
 */
export function saveFilter(data: SaveFilterPayload): Promise<TaskFilterPreset> {
  return apiClient
    .post<ApiResponse<TaskFilterPreset>>('/tasks/filters', data)
    .then((r) => r.data.data);
}

// ─── Task filter tab persistence (legacy parity) ─────────────────────────────

/**
 * Load the user's persisted task filter tab (flTasks) from users_filters_other.
 * Returns a number (1=All, 2=Upcoming, 3=Due Today, 4=Past Due, 5=Completed, 6=Inactive).
 */
export function getTaskFilterTab(): Promise<number> {
  return apiClient
    .get<ApiResponse<{ flTasks: number }>>('/tasks/filters/tab')
    .then((r) => r.data.data?.flTasks ?? 1);
}

/**
 * Save the user's selected task filter tab to users_filters_other.flTasks.
 */
export function saveTaskFilterTab(type: number): Promise<void> {
  return apiClient
    .post('/tasks/filters', { type })
    .then(() => undefined);
}

// ─── Task log ─────────────────────────────────────────────────────────────────

/**
 * Fetch the task activity log with optional filters.
 */
export function getLog(
  params: TaskLogFiltersParams = {},
): Promise<PaginatedResponse<TaskLog>> {
  return apiClient
    .get<PaginatedResponse<TaskLog>>('/tasks/log', { params })
    .then((r) => r.data);
}
